import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { fmtAED, fmtDate } from '../lib/format';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Star, Plus, Phone, Gift } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const TIER_COLOR = { Platinum: 'bg-slate-700 text-white', Gold: 'bg-amber-500 text-white', Silver: 'bg-slate-300 text-slate-800' };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', birthday: '', address: '' });
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const [c, f] = await Promise.all([api.get('/customers', { params: q ? { q } : {} }), api.get('/feedback')]);
    setCustomers(c.data); setFeedback(f.data);
  };
  useEffect(() => { load(); }, [q]);

  const add = async () => { await api.post('/customers', form); setOpen(false); setForm({ name: '', phone: '', email: '', birthday: '', address: '' }); load(); toast.success('Customer added'); };
  const openDetail = async (c) => { const { data } = await api.get(`/customers/${c.id}`); setDetail(data); };
  const redeem = async (c, pts) => { try { const { data } = await api.post(`/customers/${c.id}/redeem`, { points: pts }); toast.success(`Redeemed ${pts} pts = ${fmtAED(data.value_aed)}`); openDetail(c); load(); } catch (e) { toast.error('Insufficient points'); } };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHelp id="customers">Customers earn loyalty points automatically when attached to a bill at the POS. Open a customer to see lifetime value & order history, and redeem points. The <b>Feedback</b> tab shows guest star ratings.</PageHelp>
      <div className="flex items-center justify-between"><h1 className="font-display text-2xl font-bold">Customers & Loyalty</h1><Button data-testid="add-customer-button" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Add</Button></div>
      <Tabs defaultValue="customers">
        <TabsList><TabsTrigger value="customers">Customers</TabsTrigger><TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger></TabsList>
        <TabsContent value="customers" className="space-y-3 pt-3">
          <Input data-testid="customer-search" placeholder="Search name or phone…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {customers.map((c) => (
              <Card key={c.id} className="p-4 cursor-pointer hover:shadow-md transition-shadow" data-testid={`customer-card-${c.phone}`} onClick={() => openDetail(c)}>
                <div className="flex items-start justify-between">
                  <div><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</div></div>
                  <Badge className={TIER_COLOR[c.tier] || ''}>{c.tier}</Badge>
                </div>
                <div className="flex justify-between mt-3 text-sm">
                  <div className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-amber-500" />{c.points} pts</div>
                  <div className="text-muted-foreground">{c.visits} visits</div>
                  <div className="font-mono">{fmtAED(c.total_spent)}</div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="feedback" className="space-y-2 pt-3">
          {feedback.length === 0 && <div className="text-muted-foreground text-sm">No feedback yet.</div>}
          {feedback.map((f) => (
            <Card key={f.id} className="p-3 flex items-center justify-between">
              <div><div className="flex gap-0.5">{[1,2,3,4,5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= f.rating ? 'text-amber-500 fill-amber-500' : 'text-muted'}`} />)}</div><div className="text-sm mt-1">{f.comment || 'No comment'}</div></div>
              <div className="text-xs text-muted-foreground">{fmtDate(f.created_at)}</div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="cust-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Phone</Label><Input data-testid="cust-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Birthday</Label><Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} /></div>
            <div><Label>Delivery Address</Label><Input data-testid="cust-address" placeholder="Building, street, area, city" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <Button data-testid="cust-save" className="w-full" onClick={add}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (<>
            <DialogHeader><DialogTitle>{detail.customer.name}</DialogTitle></DialogHeader>
            <div className="flex gap-3 flex-wrap text-sm">
              <Badge className={TIER_COLOR[detail.customer.tier]}>{detail.customer.tier}</Badge>
              <span className="flex items-center gap-1"><Star className="h-4 w-4 text-amber-500" />{detail.customer.points} pts</span>
              <span>{detail.customer.visits} visits</span>
              <span className="font-mono">{fmtAED(detail.customer.total_spent)} lifetime</span>
            </div>
            <Button size="sm" variant="outline" className="w-fit" data-testid="redeem-button" onClick={() => redeem(detail.customer, 100)}><Gift className="h-4 w-4 mr-1" /> Redeem 100 pts</Button>
            <div className="text-sm font-medium mt-2">Recent Orders</div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {detail.orders.map((o) => <div key={o.id} className="flex justify-between text-sm border-b py-1"><span className="font-mono">{o.order_no}</span><span>{fmtAED(o.grand_total)}</span></div>)}
              {detail.orders.length === 0 && <div className="text-muted-foreground text-sm">No orders yet</div>}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
